
import React from 'react';
import { Link } from 'react-router-dom';
import { GitBranch, ArrowRight, Settings } from 'lucide-react';
import FadeIn from '../../../components/FadeIn';
import SEO from '../../../components/SEO';

const CrmAutomation = () => {
    return (
        <div className="bg-white">
            <SEO
                title="CRM Integration & Automation Training | BPO Consultants"
                description="Streamline your sales ops. best practices for Zapier, Make.com, and HubSpot automation."
                keywords="CRM Automation, HubSpot Training, Salesforce Automation, Zapier for Sales, Sales Operations Course"
            />

            {/* Hero */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0B0D15] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200" alt="Automation Workflow" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <span className="inline-block bg-[#A2CB52]/20 text-[#A2CB52] px-4 py-2 rounded-full text-sm font-bold border border-[#A2CB52]/40 mb-6 backdrop-blur-sm">
                            <Settings size={14} className="inline mr-1" /> Module 4: Operations
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            CRM Integration <br /> & Automation
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Eliminate manual data entry. Build a self-driving sales machine.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <FadeIn className="mb-16">
                        <h2 className="text-3xl font-bold text-[#20222D] mb-6">The "Zero-Admin" Sales Rep</h2>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                            Sales reps spend only 34% of their time selling. The rest is lost to admin. This module teaches you how to reclaim that time using powerful automation platforms like Zapier and Make.com.
                        </p>

                        <div className="grid md:grid-cols-2 gap-8">
                            {[
                                {
                                    title: "Bi-Directional Sync",
                                    desc: "Ensuring your email, calendar, and LinkedIn activity automatically logs to the CRM."
                                },
                                {
                                    title: "Automated Triggers",
                                    desc: "Setting up workflows: When a prospect clicks a link -> Notify rep on Slack -> Create Task."
                                },
                                {
                                    title: "Data Hygiene",
                                    desc: "Using automation to spot duplicates and enforce standard formatting for phone numbers and titles."
                                },
                                {
                                    title: "Reporting Dashboards",
                                    desc: "Building real-time visualizations of your pipeline health without manual spreadsheet work."
                                }
                            ].map((item, i) => (
                                <div key={i} className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                                    <div className="text-[#A2CB52] mb-4">
                                        <GitBranch size={28} />
                                    </div>
                                    <h3 className="text-xl font-bold text-[#20222D] mb-2">{item.title}</h3>
                                    <p className="text-gray-600 text-sm">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </FadeIn>

                    {/* SGE / FAQ Section */}
                    <FadeIn className="mb-16">
                        <h2 className="text-2xl font-bold text-[#20222D] mb-6">Common Questions</h2>
                        <div className="space-y-4">
                            <div className="border-b border-gray-200 pb-4">
                                <h3 className="font-bold text-gray-900 mb-2">Is this technical?</h3>
                                <p className="text-gray-600">It involves "Low-Code" tools. You don't need to be a developer, but you need to understand logic (If This, Then That).</p>
                            </div>
                            <div className="border-b border-gray-200 pb-4">
                                <h3 className="font-bold text-gray-900 mb-2">What happens if an API breaks?</h3>
                                <p className="text-gray-600">We teach error handling and monitoring so you know immediately if a workflow fails.</p>
                            </div>
                        </div>
                    </FadeIn>

                    <FadeIn className="bg-[#20222D] rounded-3xl p-12 text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#A2CB52] blur-[100px] opacity-20 pointer-events-none"></div>
                        <h2 className="text-3xl font-bold text-white mb-6">Automate Your Success</h2>
                        <Link to="/contact" className="inline-flex items-center bg-[#A2CB52] text-[#20222D] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#B5D575] transition-all transform hover:scale-105">
                            Start Module <ArrowRight className="ml-2" size={20} />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
};

export default CrmAutomation;
